import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HistoryLookupRepository } from './historyLookup.repository';
import { ColDef } from 'ag-grid-community';
import { AgGridFilterParams, AgGridColumns } from '../../Compliance/AgGrid';
import { WeissmanTimestampMsecFormatPipe } from '../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';

@Injectable()
export class HistoryLookupService {
    constructor(
        private readonly _historyLookupRepo: HistoryLookupRepository,
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
    ) {}

    tableOptions: Core.IndexedHistoryTableModel[] = [];

    async getTableOptions(): Promise<Core.IndexedHistoryTableModel[]> {
        if (this.tableOptions.length > 0) {
            return this.tableOptions;
        }
        this.tableOptions = await lastValueFrom(this._historyLookupRepo.getTableOptions());
        return this.tableOptions;
    }

    async lookupHistoryAccountNumber(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultAccountNumberModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryAccountNumber(searchModel));
    }

    async lookupHistoryAddress(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultAddressModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryAddress(searchModel));
    }

    async lookupHistoryClientServiceResponsibility(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultClientServiceResponsibilityModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryClientServiceResponsibility(searchModel));
    }

    async lookupHistoryCompany(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultCompanyModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryCompany(searchModel));
    }

    async lookupHistoryContact(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultContactModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryContact(searchModel));
    }

    async lookupHistoryGLAccount(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultGLAccountModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryGLAccount(searchModel));
    }

    async lookupHistoryParcel(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultParcelModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcel(searchModel));
    }

    async lookupHistoryParcelAssessmentClass(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelAssessmentClassModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcelAssessmentClass(searchModel));
    }

    async lookupHistoryParcelCollector(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcelCollector(searchModel));
    }

    async lookupHistoryParcelCollectorPaymentOption(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorPaymentOptionModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcelCollectorPaymentOption(searchModel));
    }

    async lookupHistoryParcelCollectorTaxAuthority(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorTaxAuthorityModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcelCollectorTaxAuthority(searchModel));
    }

    async lookupHistoryParcelDescriptor(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelDescriptorModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryParcelDescriptor(searchModel));
    }

    async lookupHistorySite(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultSiteModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistorySite(searchModel));
    }

    async lookupHistorySiteDescriptor(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultSiteDescriptorModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistorySiteDescriptor(searchModel));
    }

    async lookupHistoryTaxAuthority(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTaxAuthorityModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryTaxAuthority(searchModel));
    }

    async lookupHistoryTaxRateArea(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTaxRateAreaModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryTaxRateArea(searchModel));
    }

    async lookupHistoryTransaction(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTransactionModel[]> {
        return await lastValueFrom(this._historyLookupRepo.lookupHistoryTransaction(searchModel));
    }

    async historyLookupAccountNumber(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultAccountNumberModel[]> {
        return await this.lookupHistoryAccountNumber(searchModel);
    }

    async historyLookupAddress(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultAddressModel[]> {
        return await this.lookupHistoryAddress(searchModel);
    }

    async historyLookupClientServiceResponsibility(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultClientServiceResponsibilityModel[]> {
        return await this.lookupHistoryClientServiceResponsibility(searchModel);
    }

    async historyLookupCompany(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultCompanyModel[]> {
        return await this.lookupHistoryCompany(searchModel);
    }

    async historyLookupContact(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultContactModel[]> {
        return await this.lookupHistoryContact(searchModel);
    }

    async historyLookupGLAccount(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultGLAccountModel[]> {
        return await this.lookupHistoryGLAccount(searchModel);
    }

    async historyLookupParcel(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultParcelModel[]> {
        return await this.lookupHistoryParcel(searchModel);
    }

    async historyLookupParcelAssessmentClass(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelAssessmentClassModel[]> {
        return await this.lookupHistoryParcelAssessmentClass(searchModel);
    }

    async historyLookupParcelCollector(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorModel[]> {
        return await this.lookupHistoryParcelCollector(searchModel);
    }

    async historyLookupParcelCollectorPaymentOption(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorPaymentOptionModel[]> {
        return await this.lookupHistoryParcelCollectorPaymentOption(searchModel);
    }

    async historyLookupParcelCollectorTaxAuthority(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelCollectorTaxAuthorityModel[]> {
        return await this.lookupHistoryParcelCollectorTaxAuthority(searchModel);
    }

    async historyLookupParcelDescriptor(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultParcelDescriptorModel[]> {
        return await this.lookupHistoryParcelDescriptor(searchModel);
    }

    async historyLookupSite(searchModel: Core.HistoryLookupSearchModel): Promise<Core.HistoryResultSiteModel[]> {
        return await this.lookupHistorySite(searchModel);
    }

    async historyLookupSiteDescriptor(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultSiteDescriptorModel[]> {
        return await this.lookupHistorySiteDescriptor(searchModel);
    }

    async historyLookupTaxAuthority(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTaxAuthorityModel[]> {
        return await this.lookupHistoryTaxAuthority(searchModel);
    }

    async historyLookupTaxRateArea(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTaxRateAreaModel[]> {
        return await this.lookupHistoryTaxRateArea(searchModel);
    }

    async historyLookupTransaction(
        searchModel: Core.HistoryLookupSearchModel,
    ): Promise<Core.HistoryResultTransactionModel[]> {
        return await this.lookupHistoryTransaction(searchModel);
    }

    getBaseHistoryColumnDefs(): ColDef[] {
        const columns: ColDef[] = [
            {
                headerName: 'History ID',
                field: 'historyID',
                width: AgGridColumns.textColumnSmallWidth,
                maxWidth: AgGridColumns.textColumnMedWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
            },
            {
                headerName: 'Operation',
                field: 'operation',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'User ID',
                field: 'operationUser',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'User Name',
                field: 'operationUserName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'User Email',
                field: 'operationUserEmail',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Timestamp',
                field: 'operationDate',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.HistoryResultBaseModel;
                    if (!(model && model.operationDate)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.operationDate);
                },
            },
        ];
        return columns;
    }
}
